import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faTrash, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useAdminUsers } from '../../../hooks/useAdmin';
import {
  useShareList,
  useCreateShare,
  useDeleteShare,
  useEmbedTokenList,
  useCreateEmbedToken,
  useDeleteEmbedToken,
} from '../hooks/useDashboards';
import type { AdminUser } from '../../../types/admin';
import styles from './ShareDialog.module.css';

interface ShareDialogProps {
  dashboardId: number;
  dashboardName: string;
  onClose: () => void;
}

export default function ShareDialog({ dashboardId, dashboardName, onClose }: ShareDialogProps) {
  const [tab, setTab] = useState<'share' | 'embed'>('share');

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Share "{dashboardName}"</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === 'share' ? styles.tabActive : ''}`}
            onClick={() => setTab('share')}
          >
            People
          </button>
          <button
            className={`${styles.tab} ${tab === 'embed' ? styles.tabActive : ''}`}
            onClick={() => setTab('embed')}
          >
            Embed Link
          </button>
        </div>

        <div className={styles.body}>
          {tab === 'share' ? (
            <ShareTab dashboardId={dashboardId} />
          ) : (
            <EmbedTab dashboardId={dashboardId} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Share Tab ──────────────────────────────────────────────────────────────── */

function ShareTab({ dashboardId }: { dashboardId: number }) {
  const { data: usersData } = useAdminUsers();
  const { data: sharesData, isLoading } = useShareList(dashboardId);
  const createShare = useCreateShare(dashboardId);
  const deleteShare = useDeleteShare(dashboardId);

  const [selectedUser, setSelectedUser] = useState('');
  const [permission, setPermission] = useState<'view' | 'edit'>('view');

  const shares = sharesData?.data || [];
  const users: AdminUser[] = usersData || [];

  // Filter out users who already have a share
  const sharedUsernames = new Set(shares.map((s) => s.shared_with));
  const availableUsers = users.filter((u) => !sharedUsernames.has(u.username));

  const handleAdd = () => {
    if (!selectedUser) return;
    createShare.mutate(
      { shared_with: selectedUser, permission },
      { onSuccess: () => setSelectedUser('') },
    );
  };

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Add person</div>
        <div className={styles.addRow}>
          <select
            className={styles.userSelect}
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <option value="">Select a user...</option>
            {availableUsers.map((u) => (
              <option key={u.username} value={u.username}>
                {u.full_name || u.email || u.username}
              </option>
            ))}
          </select>
          <select
            className={styles.permSelect}
            value={permission}
            onChange={(e) => setPermission(e.target.value as 'view' | 'edit')}
          >
            <option value="view">View</option>
            <option value="edit">Edit</option>
          </select>
          <button
            className={styles.addBtn}
            onClick={handleAdd}
            disabled={!selectedUser || createShare.isPending}
          >
            Share
          </button>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Shared with</div>
        {isLoading ? (
          <p className={styles.emptyMsg}>Loading...</p>
        ) : shares.length === 0 ? (
          <p className={styles.emptyMsg}>Not shared with anyone yet</p>
        ) : (
          <ul className={styles.shareList}>
            {shares.map((share) => {
              const user = users.find((u) => u.username === share.shared_with);
              return (
                <li key={share.id} className={styles.shareItem}>
                  <div className={styles.shareUser}>
                    <span className={styles.shareName}>
                      {user?.full_name || user?.email || share.shared_with}
                    </span>
                    {user?.email && <span className={styles.shareEmail}>{user.email}</span>}
                  </div>
                  <div className={styles.shareActions}>
                    <span className={styles.permBadge}>{share.permission}</span>
                    <button
                      className={styles.revokeBtn}
                      onClick={() => deleteShare.mutate(share.id)}
                      title="Revoke access"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/* ─── Embed Tab ──────────────────────────────────────────────────────────────── */

function EmbedTab({ dashboardId }: { dashboardId: number }) {
  const { data: embedsData, isLoading } = useEmbedTokenList(dashboardId);
  const createEmbed = useCreateEmbedToken(dashboardId);
  const deleteEmbed = useDeleteEmbedToken(dashboardId);

  const [label, setLabel] = useState('');
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const embeds = embedsData?.data || [];

  const handleCreate = () => {
    createEmbed.mutate(
      { label: label || undefined },
      {
        onSuccess: (data) => {
          setLabel('');
          const embedUrl = `${window.location.origin}/bi/embed/${data.token}`;
          setRevealedToken(embedUrl);
        },
      },
    );
  };

  const handleCopy = () => {
    if (!revealedToken) return;
    navigator.clipboard.writeText(revealedToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Generate embed link</div>
        <div className={styles.embedCreate}>
          <input
            className={styles.embedLabelInput}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (optional)"
          />
          <button className={styles.addBtn} onClick={handleCreate} disabled={createEmbed.isPending}>
            Generate
          </button>
        </div>

        {revealedToken && (
          <div className={styles.tokenReveal}>
            <p className={styles.tokenWarning}>Copy this link now — it won't be shown again.</p>
            <p className={styles.tokenUrl}>{revealedToken}</p>
            <button className={styles.copyBtn} onClick={handleCopy}>
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} />{' '}
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Active embed links</div>
        {isLoading ? (
          <p className={styles.emptyMsg}>Loading...</p>
        ) : embeds.length === 0 ? (
          <p className={styles.emptyMsg}>No embed links created yet</p>
        ) : (
          <ul className={styles.embedList}>
            {embeds.map((embed) => (
              <li key={embed.id} className={styles.embedItem}>
                <div>
                  <span className={styles.embedLabel}>{embed.label || `Embed #${embed.id}`}</span>
                  <div className={styles.embedMeta}>
                    Created {new Date(embed.created_at).toLocaleDateString()}
                    {embed.expires_at && (
                      <> · Expires {new Date(embed.expires_at).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
                <button
                  className={styles.revokeBtn}
                  onClick={() => deleteEmbed.mutate(embed.id)}
                  title="Revoke embed link"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
